import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, Button, Form, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';

const SkillSuggestion = () => {
  const [month, setMonth] = useState('');
  const [months, setMonths] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [playerRes, monthRes] = await Promise.all([
          api.get('/api/players/public'),
          api.get('/api/stats/months')
        ]);
        setPlayers(playerRes.data);
        setMonths(monthRes.data);
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    fetchInitialData();
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/stats/suggestions?month=${month}`);
      setSuggestions(res.data);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getArrowColor = (oldSkill, newSkill) => {
    if (newSkill == null) return 'text-muted';
    if (oldSkill == null) return 'text-primary';
    if (newSkill > oldSkill) return 'text-success';
    if (newSkill < oldSkill) return 'text-danger';
    return 'text-warning';
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Skill Rating Suggestions</h2>
      <Form className="d-flex align-items-center gap-2 mb-4">
        <Form.Select value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="">Select a month</option>
          {months.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </Form.Select>
        <Button onClick={fetchSuggestions} disabled={!month || loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Calculate New Rating'}
        </Button>
      </Form>

      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-4">
        <AnimatePresence>
          {suggestions.map((s, idx) => {
            const player = players.find(p => p.name === s.playerName);
            const oldSkill = player?.skillLevel;
            const newSkill = s.suggestedSkill;
            const arrowColor = getArrowColor(oldSkill, newSkill);

            return (
              <div className="col" key={idx}>
                <Card className="h-100 text-center shadow">
                  <Card.Body>
                    <Card.Title>{s.playerName}</Card.Title>
                    {newSkill == null ? (
                      <p className="text-muted mt-2">Not enough games played</p>
                    ) : (
                    <motion.div
                        className="d-flex justify-content-center align-items-center fs-5 mt-2"
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 30, opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      >
                        <span className="me-2">{oldSkill ?? '?'}</span>
                        <span className={`mx-1 ${arrowColor}`}>→</span>
                        <span className="ms-2">{newSkill}</span>
                      </motion.div>
                    )}
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SkillSuggestion;
